import { useState, useEffect, useRef } from 'react'
import { Bell, X, Check, ShoppingBag, Truck, MessageSquare, Info } from 'lucide-react'
import { useAuthStore } from '@/lib/store'
import { supabase } from '@/lib/supabase'
import { Button } from '@/components/ui/button'
import { Link } from 'react-router-dom'

interface Notification {
  id: string
  title: string
  content: string
  type: 'system' | 'order' | 'chat' | 'application'
  read: boolean
  created_at: string
  metadata: Record<string, any>
}

export function NotificationCenter() {
  const user = useAuthStore((state) => state.user)
  const [notifications, setNotifications] = useState<Notification[]>([])
  const [unreadCount, setUnreadCount] = useState(0)
  const [isOpen, setIsOpen] = useState(false)
  const [loading, setLoading] = useState(false)
  const notificationRef = useRef<HTMLDivElement>(null)
  const [hasNewNotification, setHasNewNotification] = useState(false)

  useEffect(() => {
    if (user) {
      fetchNotifications()
      
      // Set up real-time subscription
      const channel = supabase
        .channel('notifications')
        .on(
          'postgres_changes',
          {
            event: 'INSERT',
            schema: 'public',
            table: 'notifications',
            filter: `user_id=eq.${user.id}`
          },
          (payload) => {
            // Add the new notification to the list
            const newNotification = payload.new as Notification
            setNotifications(prev => [newNotification, ...prev])
            setUnreadCount(prev => prev + 1)
            setHasNewNotification(true)
            
            // Show notification badge animation
            playNotificationSound()
            showBrowserNotification(newNotification)
          }
        )
        .subscribe()

      // Clean up subscription on unmount
      return () => {
        channel.unsubscribe()
      }
    }
  }, [user])

  // Close notification panel when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (notificationRef.current && !notificationRef.current.contains(event.target as Node)) {
        setIsOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [])

  const fetchNotifications = async () => {
    if (!user) return

    try {
      setLoading(true)
      const { data, error } = await supabase
        .from('notifications')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false })
        .limit(20)

      if (error) throw error
      
      setNotifications(data || [])
      
      // Count unread notifications
      const unread = data?.filter(n => !n.read).length || 0
      setUnreadCount(unread)
    } catch (err) {
      console.error('Error fetching notifications:', err)
    } finally {
      setLoading(false)
    }
  }

  const markAsRead = async (id: string) => {
    if (!user) return

    try {
      const { error } = await supabase
        .from('notifications')
        .update({ read: true })
        .eq('id', id)

      if (error) throw error

      // Update local state
      setNotifications(prev => 
        prev.map(n => n.id === id ? { ...n, read: true } : n)
      )
      setUnreadCount(prev => Math.max(0, prev - 1))
    } catch (err) {
      console.error('Error marking notification as read:', err)
    }
  }

  const markAllAsRead = async () => {
    if (!user) return

    try {
      const { error } = await supabase
        .from('notifications')
        .update({ read: true })
        .eq('user_id', user.id)
        .eq('read', false)

      if (error) throw error

      // Update local state
      setNotifications(prev => prev.map(n => ({ ...n, read: true })))
      setUnreadCount(0)
    } catch (err) {
      console.error('Error marking all notifications as read:', err)
    }
  }

  const getNotificationIcon = (type: string) => {
    switch (type) {
      case 'order':
        return <ShoppingBag className="h-5 w-5 text-blue-500" />
      case 'chat':
        return <MessageSquare className="h-5 w-5 text-green-500" />
      case 'application':
        return <Check className="h-5 w-5 text-purple-500" />
      case 'system':
      default:
        return <Info className="h-5 w-5 text-gray-500" />
    }
  }

  const getNotificationLink = (notification: Notification) => {
    const metadata = notification.metadata || {}
    
    switch (notification.type) {
      case 'order':
        return metadata.order_id ? `/orders/tracking/${metadata.order_id}` : '/profile'
      case 'chat':
        return '/messages'
      case 'application':
        if (metadata.seller_application) return '/seller/application-status'
        if (metadata.delivery_application) return '/delivery/application-status'
        return '/profile'
      default:
        return '#'
    }
  }

  const formatDate = (dateString: string) => {
    const date = new Date(dateString)
    const now = new Date()
    const diffMs = now.getTime() - date.getTime()
    const diffMins = Math.round(diffMs / 60000)
    const diffHours = Math.round(diffMs / 3600000)
    const diffDays = Math.round(diffMs / 86400000)
    
    if (diffMins < 60) {
      return `${diffMins} min${diffMins !== 1 ? 's' : ''} ago`
    } else if (diffHours < 24) {
      return `${diffHours} hour${diffHours !== 1 ? 's' : ''} ago`
    } else if (diffDays < 7) {
      return `${diffDays} day${diffDays !== 1 ? 's' : ''} ago`
    } else {
      return date.toLocaleDateString()
    }
  }

  const playNotificationSound = () => {
    try {
      const audio = new Audio('/notification.mp3')
      audio.volume = 0.5
      audio.play().catch(e => console.log('Audio play failed:', e))
    } catch (err) {
      console.log('Could not play notification sound')
    }
  }

  const showBrowserNotification = (notification: Notification) => {
    // Check if browser notifications are supported and permission is granted
    if ('Notification' in window && Notification.permission === 'granted') {
      new Notification('AEM Marketplace', {
        body: notification.title,
        icon: '/favicon.ico'
      })
    } else if ('Notification' in window && Notification.permission !== 'denied') {
      Notification.requestPermission()
    }
  }

  const toggleNotifications = () => {
    setIsOpen(!isOpen)
    setHasNewNotification(false)
  }

  if (!user) return null

  return (
    <div className="relative" ref={notificationRef}>
      <button
        className="relative p-2 text-white hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 rounded-full"
        onClick={toggleNotifications}
        aria-label="Notifications"
      >
        <Bell className="h-6 w-6" />
        {unreadCount > 0 && (
          <span className={`absolute top-0 right-0 inline-flex items-center justify-center px-2 py-1 text-xs font-bold leading-none text-white transform translate-x-1/2 -translate-y-1/2 rounded-full ${
            hasNewNotification ? 'bg-red-500 animate-pulse' : 'bg-primary'
          }`}>
            {unreadCount}
          </span>
        )}
      </button>

      {isOpen && (
        <div className="fixed md:absolute inset-0 md:inset-auto md:right-0 md:mt-2 w-full md:w-96 bg-white md:rounded-lg shadow-lg md:overflow-hidden z-50 flex flex-col h-full md:h-auto md:max-h-[80vh]">
          <div className="p-4 border-b border-gray-200 flex justify-between items-center">
            <h3 className="text-lg font-medium text-gray-900">Notifications</h3>
            {unreadCount > 0 && (
              <Button
                variant="ghost"
                size="sm"
                onClick={markAllAsRead}
                className="text-sm text-primary hover:text-primary-dark"
              >
                Mark all as read
              </Button>
            )}
          </div>
          
          <div className="flex-1 overflow-y-auto">
            {loading ? (
              <div className="flex justify-center items-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
              </div>
            ) : notifications.length === 0 ? (
              <div className="py-8 text-center">
                <Bell className="mx-auto h-12 w-12 text-gray-300" />
                <p className="mt-2 text-sm text-gray-500">No notifications yet</p>
              </div>
            ) : (
              <div>
                {notifications.map((notification) => (
                  <div 
                    key={notification.id} 
                    className={`p-4 border-b border-gray-100 hover:bg-gray-50 transition-colors ${
                      !notification.read ? 'bg-blue-50' : ''
                    }`}
                  >
                    <Link 
                      to={getNotificationLink(notification)}
                      className="block"
                      onClick={() => {
                        if (!notification.read) {
                          markAsRead(notification.id)
                        }
                        setIsOpen(false)
                      }}
                    >
                      <div className="flex">
                        <div className="flex-shrink-0 mt-0.5">
                          {getNotificationIcon(notification.type)}
                        </div>
                        <div className="ml-3 flex-1">
                          <div className="flex justify-between items-start">
                            <p className="text-sm font-medium text-gray-900">{notification.title}</p>
                            <span className="text-xs text-gray-500">{formatDate(notification.created_at)}</span>
                          </div>
                          <p className="mt-1 text-sm text-gray-600">{notification.content}</p>
                        </div>
                      </div>
                    </Link>
                    {!notification.read && (
                      <div className="mt-2 flex justify-end">
                        <button
                          onClick={() => markAsRead(notification.id)}
                          className="text-xs text-primary hover:text-primary-dark"
                        >
                          Mark as read
                        </button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
          
          <div className="p-3 border-t border-gray-200 bg-gray-50 mt-auto">
            <button
              onClick={() => setIsOpen(false)}
              className="w-full text-center text-sm text-gray-500 hover:text-gray-700"
            >
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  )
}